import {
  Filtering,
  QSpinner,
  QText,
  useCurrentUser,
} from '@qualio/ui-components';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentAccount,
  isDocumentAccount,
} from '../../../../../api/model/document';
import { useTags } from '../../../../../hooks/useTags';
import { keyById } from '../../../../../util/keyById';
import { useTemplates } from '../../../../../hooks/useTemplates';
import { useUsers } from '../../../../../hooks/useUsers';
import { TagLabel } from '../../../TagLabel/TagLabel';
import { actionValueLabelMap, statusValueLabelMap } from '../constants';
import TypeItemlabel from '../../../TableFilters/TypeItemLabel';
import { WorkspaceTabs } from '../WorkspaceV2';
import { useWorkspaceAccountFilter } from './useWorkspaceAccountFilter';

export const useFilterDefinitions = (tab: WorkspaceTabs) => {
  const { t } = useTranslation();
  const { companyName } = useCurrentUser();
  const { data: templates = [] } = useTemplates({
    includeArchived: false,
  });
  const { data: tags = [] } = useTags();
  const { data: users = [] } = useUsers();
  const { accountFilterApplies, parentCompanyName } =
    useWorkspaceAccountFilter(tab);

  const tagsMap = useMemo(() => keyById(tags, (tag) => tag.id), [tags]);

  const templatesMap = useMemo(
    () => keyById(templates, (template) => template.prefix),
    [templates],
  );

  const usersMap = useMemo(() => keyById(users, (user) => user.id), [users]);

  const activeStatusFilterRender = useCallback((value: unknown) => {
    const id = value as string;
    const label = statusValueLabelMap[id];
    if (label) {
      return <QText>{label}</QText>;
    }
    return <QSpinner size="sm" />;
  }, []);

  const activeActionFilterRender = useCallback((value: unknown) => {
    const id = value as string;
    const label = actionValueLabelMap[id];
    if (label) {
      return <QText>{label}</QText>;
    }
    return <QSpinner size="sm" />;
  }, []);

  const activeTagFilterRender = useCallback(
    (value: unknown) => {
      const tagId = Number(value);
      const tag = tagsMap[tagId];
      if (tag) {
        return <TagLabel tag={tag} />;
      }
      return <QSpinner size="sm" />;
    },
    [tagsMap],
  );

  const activeOwnerFilterRender = useCallback(
    (value: unknown) => {
      const userId = Number(value);
      const user = usersMap[userId];
      if (user) {
        return <QText>{user.full_name}</QText>;
      }
      return <QSpinner size="sm" />;
    },
    [usersMap],
  );

  const activeTemplateFilterRender = useCallback(
    (value: unknown) => {
      const templatePrefix = String(value);
      const template = templatesMap[templatePrefix];
      if (template) {
        return (
          <TypeItemlabel
            templateId={template.id}
            templatePrefix={template.prefix}
            templateName={template.name}
            parent={template.parent}
          />
        );
      }
      return <QSpinner size="sm" />;
    },
    [templatesMap],
  );

  const activeAccountFilterRender = useCallback(
    (value: unknown) => {
      const label =
        value === DocumentAccount.Own ? companyName : parentCompanyName;
      if (label) {
        return <QText>{label}</QText>;
      }
      return <QSpinner size="sm" />;
    },
    [companyName, parentCompanyName],
  );

  return useMemo(() => {
    const commonFilters = {
      types: {
        label: t('labels.type'),
        schema: Filtering.schemas.StringSchema().array().nullable(),
        activeRender: activeTemplateFilterRender,
      },
      tags: {
        label: t('labels.tags'),
        schema: Filtering.schemas.StringSchema().array().nullable(),
        activeRender: activeTagFilterRender,
      },
      owner: {
        label: t('labels.owner'),
        schema: Filtering.schemas.StringSchema().nullable(),
        activeRender: activeOwnerFilterRender,
      },
    };

    if (tab === WorkspaceTabs.YOUR_ACTIONS) {
      return {
        action: {
          label: t('labels.action'),
          schema: Filtering.schemas.StringSchema().nullable(),
          defaultValue: 'all_actions',
          activeRender: activeActionFilterRender,
        },
        ...commonFilters,
      };
    }

    return {
      status: {
        label: t('labels.status'),
        schema: Filtering.schemas.StringSchema().nullable(),
        defaultValue: 'all_open',
        activeRender: activeStatusFilterRender,
      },
      ...commonFilters,
      ...(accountFilterApplies && {
        account: {
          label: t('labels.account'),
          schema: Filtering.schemas
            .StringSchema((schema) => schema.refine(isDocumentAccount))
            .nullable(),
          activeRender: activeAccountFilterRender,
        },
      }),
    };
  }, [
    activeTemplateFilterRender,
    activeStatusFilterRender,
    activeActionFilterRender,
    activeTagFilterRender,
    activeOwnerFilterRender,
    activeAccountFilterRender,
    accountFilterApplies,
    t,
    tab,
  ]);
};
